import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { formatCurrency } from '../utils';

// Componente de overlay (fondo oscuro)
const Overlay: React.FC<{ onClose: () => void }> = ({ onClose }) => (
  <div 
    className="fixed inset-0 bg-black bg-opacity-50 z-40 transition-opacity duration-300"
    onClick={onClose}
  ></div>
);

// Componente de ítem del carrito
interface CartItemProps {
  id: number;
  name: string;
  variantLabel?: string;
  price: number;
  quantity: number;
  image?: string;
  onUpdateQuantity: (id: number, quantity: number) => void;
  onRemove: (id: number) => void;
}

const CartItem: React.FC<CartItemProps> = ({ 
  id, 
  name, 
  variantLabel,
  price, 
  quantity, 
  image, 
  onUpdateQuantity, 
  onRemove 
}) => {
  return (
    <div className="flex items-center space-x-4 py-4 border-b border-gray-100">
      <div className="flex-shrink-0 w-16 h-16 bg-gray-100 rounded-lg overflow-hidden">
        {image ? (
          <img src={image} alt={name} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <span className="material-icons text-gray-400">image</span>
          </div>
        )}
      </div>
      
      <div className="flex-grow">
        <h3 className="font-medium text-gray-800 text-sm">{name}</h3>
        {variantLabel && (
          <p className="text-xs text-gray-500">Variante: {variantLabel}</p>
        )}
        <p className="text-primary-accent font-semibold">{formatCurrency(typeof price === 'number' ? price : Number(price ?? 0))}</p>
        
        <div className="flex items-center space-x-2 mt-2">
          <button
            onClick={() => onUpdateQuantity(id, Math.max(1, quantity - 1))}
            className="w-6 h-6 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200 transition-colors"
          >
            <span className="material-icons text-xs">remove</span>
          </button>
          <span className="text-sm font-medium w-8 text-center">{quantity}</span>
          <button
            onClick={() => onUpdateQuantity(id, quantity + 1)}
            className="w-6 h-6 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200 transition-colors"
          >
            <span className="material-icons text-xs">add</span>
          </button>
        </div>
      </div>
      
      <div className="flex flex-col items-end space-y-2">
        <button
          onClick={() => onRemove(id)}
          className="text-gray-400 hover:text-red-500 transition-colors"
        >
          <span className="material-icons text-sm">delete</span>
        </button>
        <span className="font-semibold text-gray-800 text-sm">
          {formatCurrency(Number(price ?? 0) * quantity)}
        </span>
      </div>
    </div>
  );
};

// Componente principal del Sidebar
export const CartSidebar: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const { cartItems, loading, removeItemFromCart, updateItemQuantity, closeCart } = useCart();
  const navigate = useNavigate();

  // Calcular subtotal
  const subtotal = cartItems.reduce((sum, item) => {
    const unit = Number(((item as any)?.unit_price ?? item.price ?? 0));
    const totalItem = Number(((item as any)?.total_price ?? unit * item.quantity));
    return sum + totalItem;
  }, 0);

  const handleUpdateQuantity = (id: number, quantity: number) => {
    updateItemQuantity(id, quantity);
  };

  const handleRemoveItem = (id: number) => {
    removeItemFromCart(id);
  };

  const handleCheckout = () => {
    closeCart();
    navigate('/checkout');
  };

  return (
    <>
      <Overlay onClose={onClose} />
      
      {/* Panel del Carrito */}
      <div className="fixed top-0 right-0 w-full md:w-96 h-full bg-white shadow-xl z-50 flex flex-col animate-slide-in">
        
        {/* 1. Header del Carrito */}
        <div className="flex justify-between items-center p-6 border-b border-gray-200 bg-white">
          <h2 className="text-xl font-semibold text-text-main font-serif">Tu Carrito</h2>
          <button 
            onClick={onClose}
            className="text-gray-500 hover:text-text-main transition-colors p-1 rounded-full hover:bg-gray-100"
          >
            <span className="material-icons">close</span>
          </button>
        </div>

        {/* 2. Cuerpo del Carrito (Contenido) */}
        <div className="flex-grow overflow-y-auto bg-primary-light">
          {loading ? (
            <div className="flex flex-col items-center justify-center h-full text-gray-500">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-accent mb-4"></div>
              <p>Cargando carrito...</p>
            </div>
          ) : cartItems.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-gray-500">
              <span className="material-icons text-6xl text-gray-300 mb-4">
                shopping_bag
              </span>
              <p className="text-lg font-medium">Tu carrito está vacío</p>
              <p className="text-sm text-gray-400 mt-2">¡Agrega algunos productos!</p>
            </div>
          ) : (
            <div className="p-4">
                  {cartItems.map((item) => {
                    const pv = (item as any)?.product_variant;
                    const variantLabel = (item as any)?.variant_name
                      || pv?.name
                      || item.variant?.name;
                return (
                  <CartItem
                    key={item.id}
                    id={item.id}
                    name={item.product.name}
                    variantLabel={variantLabel}
                    price={Number(((item as any)?.unit_price ?? item.price ?? 0))}
                    quantity={item.quantity}
                    image={item.product.image}
                    onUpdateQuantity={handleUpdateQuantity}
                    onRemove={handleRemoveItem}
                  />
                );
              })}
            </div>
          )}
        </div>

        {/* 3. Footer del Carrito (Checkout) */}
        {cartItems.length > 0 && (
          <div className="p-6 border-t border-gray-200 bg-white">
            <div className="flex justify-between items-center mb-4">
              <span className="text-lg font-semibold text-text-main">Subtotal:</span>
              <span className="text-lg font-bold text-primary-accent">{formatCurrency(subtotal)}</span>
            </div>
            <div className="space-y-3">
              <button
                onClick={handleCheckout}
                className="w-full bg-primary-accent text-white py-3 rounded-lg font-semibold hover:bg-opacity-90 transition-all duration-200 flex items-center justify-center space-x-2"
              >
                <span className="material-icons text-sm">shopping_cart_checkout</span>
                <span>Ir a Pagar</span>
              </button>
              <button
                onClick={onClose}
                className="w-full bg-gray-100 text-gray-700 py-2 rounded-lg font-medium hover:bg-gray-200 transition-colors"
              >
                Seguir Comprando
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
};